import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Zap, Brain, Users, Mail, BarChart3, Search, 
  MessageSquare, Calendar, TrendingUp, Database,
  Settings, Shield, Play, ArrowRight, ChevronDown,
  ChevronUp, Clock, Target, Sparkles, Check, Inbox, PenTool
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  detailedDescription: string;
  icon: React.ReactNode;
  replaces: string;
  replacesPrice: string;
  videoUrl?: string;
  demoImage?: string;
  stats?: {
    label: string;
    value: string;
  };
  features: string[];
  howItWorks: string[];
}

const products: Product[] = [
  {
    id: 'auto-launch',
    name: 'Auto-Launch',
    tagline: 'From URL to 50 Meetings',
    description: 'Paste product link. AI books 50+ meetings in 7 days.',
    detailedDescription: 'Our flagship feature that turns a simple product description into a full email campaign. Just paste your product info and watch as AI builds your entire outreach strategy.',
    icon: <Zap className="w-6 h-6" />,
    replaces: 'SDR Team',
    replacesPrice: '$8K+/mo',
    videoUrl: 'https://www.loom.com/embed/8a205d85ee8f42edb9f90ea1a4483d6e',
    stats: {
      label: 'Setup time',
      value: '2 minutes'
    },
    features: [
      'One-click campaign creation',
      'Automatic ICP detection',
      'Multi-variant copy generation',
      'Smart send time optimization'
    ],
    howItWorks: [
      'Paste your product/service description',
      'AI analyzes and creates buyer personas',
      'Generates 10+ email variations',
      'Launches campaign automatically'
    ]
  },
  {
    id: 'ai-prospector',
    name: 'AI Prospector',
    tagline: 'Targets CEOs at Series-A Startups',
    description: '250M verified emails. Finds "VP Sales at 50-200 employee SaaS".',
    detailedDescription: 'Stop paying for expensive lead databases. Our AI searches through 250M+ verified contacts to find your exact ideal customer profile.',
    icon: <Search className="w-6 h-6" />,
    replaces: 'Apollo/ZoomInfo',
    replacesPrice: '$300+/mo',
    stats: {
      label: 'Contacts',
      value: '250M+'
    },
    features: [
      'Real-time email verification',
      'LinkedIn data enrichment',
      'Company technographics',
      'Buying intent signals'
    ],
    howItWorks: [
      'Define your ideal customer criteria',
      'AI searches across multiple databases',
      'Verifies all contact information',
      'Delivers ready-to-contact leads'
    ]
  },
  {
    id: 'email-composer',
    name: 'Email Composer',
    tagline: 'AI Copywriter',
    description: 'Generate personalized cold emails that actually get replies.',
    detailedDescription: 'Our AI writes better cold emails than 99% of humans. Trained on millions of successful campaigns, it knows exactly what makes prospects respond.',
    icon: <Mail className="w-6 h-6" />,
    replaces: 'Copywriter',
    replacesPrice: '$2K+/mo',
    stats: {
      label: 'Reply rate',
      value: '47%'
    },
    features: [
      'Hyper-personalization at scale',
      'A/B test variations',
      'Tone and style matching',
      'Multi-language support'
    ],
    howItWorks: [
      'AI researches each prospect',
      'Crafts personalized opening lines',
      'Writes compelling value props',
      'Optimizes for responses'
    ]
  },
  {
    id: 'smart-inbox',
    name: 'Smart Inbox',
    tagline: 'AI Response Handler',
    description: 'AI responds to every reply and books meetings automatically.',
    detailedDescription: 'Never miss a hot lead again. Our AI responds to interested prospects within minutes, handles objections, and books meetings directly on your calendar.',
    icon: <MessageSquare className="w-6 h-6" />,
    replaces: 'VA/Assistant',
    replacesPrice: '$1.5K+/mo',
    stats: {
      label: 'Response time',
      value: '<2 min'
    },
    features: [
      '24/7 instant responses',
      'Natural conversation flow',
      'Objection handling',
      'Meeting scheduling'
    ],
    howItWorks: [
      'Monitors all campaign replies',
      'AI understands intent and sentiment',
      'Crafts appropriate responses',
      'Books meetings when interest shown'
    ]
  },
  {
    id: 'meeting-booker',
    name: 'Meeting Booker',
    tagline: 'Calendar Automation',
    description: 'Automatically schedules meetings on your calendar.',
    detailedDescription: 'Seamless calendar integration that handles time zones, availability, and sends reminders. Reduces no-shows by 73%.',
    icon: <Calendar className="w-6 h-6" />,
    replaces: 'Calendly + Assistant',
    replacesPrice: '$50+/mo',
    stats: {
      label: 'No-show rate',
      value: '<10%'
    },
    features: [
      'Calendar sync (Google, Outlook)',
      'Automatic reminder sequences',
      'Time zone detection',
      'Buffer time management'
    ],
    howItWorks: [
      'Syncs with your calendar',
      'Shows availability to prospects',
      'Sends confirmation emails',
      'Follows up before meetings'
    ]
  },
  {
    id: 'self-optimizer',
    name: 'Self-Optimizer',
    tagline: 'AI Performance Engine',
    description: 'Continuously improves campaigns based on response data.',
    detailedDescription: 'While you sleep, our AI analyzes every email, every response, and every meeting booked to continuously improve your campaigns.',
    icon: <Brain className="w-6 h-6" />,
    replaces: 'Marketing Analyst',
    replacesPrice: '$4K+/mo',
    stats: {
      label: 'Improvement',
      value: '+127%'
    },
    features: [
      'Real-time performance analysis',
      'Automatic copy optimization',
      'Audience refinement',
      'Send time optimization'
    ],
    howItWorks: [
      'Tracks all campaign metrics',
      'Identifies winning patterns',
      'Tests new variations',
      'Implements improvements automatically'
    ]
  },
  {
    id: 'warmup-engine',
    name: 'Warmup Engine',
    tagline: 'Deliverability System',
    description: 'Automatic email warmup & reputation management.',
    detailedDescription: 'Enterprise-grade deliverability infrastructure that ensures your emails land in the primary inbox, not spam.',
    icon: <Shield className="w-6 h-6" />,
    replaces: 'Instantly/Lemwarm',
    replacesPrice: '$37+/mo',
    stats: {
      label: 'Inbox rate',
      value: '95%+'
    },
    features: [
      'Automatic domain warmup',
      'Reputation monitoring',
      'Spam test analysis',
      'Deliverability alerts'
    ],
    howItWorks: [
      'Gradually warms new domains',
      'Monitors sender reputation',
      'Adjusts sending patterns',
      'Maintains high deliverability'
    ]
  },
  {
    id: 'analytics-dashboard',
    name: 'Analytics Dashboard',
    tagline: 'Real-time Insights',
    description: 'Track campaigns, meetings booked, and ROI in real-time.',
    detailedDescription: 'Beautiful, actionable analytics that show you exactly what\'s working and what\'s not. Track ROI down to the penny.',
    icon: <BarChart3 className="w-6 h-6" />,
    replaces: 'Analytics Tools',
    replacesPrice: '$99+/mo',
    stats: {
      label: 'Data points',
      value: '50+'
    },
    features: [
      'Real-time campaign metrics',
      'ROI tracking',
      'A/B test results',
      'Custom reports'
    ],
    howItWorks: [
      'Collects data from all touchpoints',
      'Visualizes key metrics',
      'Identifies trends and patterns',
      'Exports reports for stakeholders'
    ]
  },
  {
    id: 'team-workspace',
    name: 'Team Workspace',
    tagline: 'Collaboration Hub',
    description: 'Manage multiple users, campaigns, and permissions.',
    detailedDescription: 'Built for teams from day one. Manage permissions, share campaigns, and scale your outreach efforts across your entire organization.',
    icon: <Users className="w-6 h-6" />,
    replaces: 'CRM Features',
    replacesPrice: '$50+/mo/user',
    stats: {
      label: 'Users',
      value: 'Unlimited'
    },
    features: [
      'Role-based permissions',
      'Campaign sharing',
      'Team performance tracking',
      'Centralized lead management'
    ],
    howItWorks: [
      'Invite team members',
      'Assign roles and permissions',
      'Share campaigns and templates',
      'Track team performance'
    ]
  },
  {
    id: 'high-deliverability-mailboxes',
    name: 'Premium Mailboxes',
    tagline: '25-50% Open Rates',
    description: 'Pre-warmed mailboxes with proven deliverability infrastructure.',
    detailedDescription: 'Get instant access to our premium mailbox infrastructure with guaranteed 25-50% open rates. No more worrying about spam folders or domain reputation.',
    icon: <Inbox className="w-6 h-6" />,
    replaces: 'Mailbox Setup',
    replacesPrice: '$200+/mo',
    stats: {
      label: 'Open rate',
      value: '25-50%'
    },
    features: [
      'Pre-warmed sending domains',
      'DMARC/SPF/DKIM configured',
      'Dedicated IP addresses',
      'Reputation monitoring'
    ],
    howItWorks: [
      'Access pre-warmed mailboxes instantly',
      'Automatic rotation for optimal delivery',
      'Real-time deliverability monitoring',
      'Guaranteed inbox placement'
    ]
  },
  {
    id: 'lead-cleaner',
    name: 'Lead Cleaner',
    tagline: 'Data Hygiene Engine',
    description: 'Automatically verify and clean your lead lists for maximum deliverability.',
    detailedDescription: 'Our AI-powered lead cleaning system removes invalid emails, duplicates, and risky contacts before they hurt your sender reputation.',
    icon: <Database className="w-6 h-6" />,
    replaces: 'Data Cleaning Tools',
    replacesPrice: '$150+/mo',
    stats: {
      label: 'Accuracy',
      value: '99.5%'
    },
    features: [
      'Real-time email verification',
      'Duplicate detection & removal',
      'Bounce risk assessment',
      'Compliance filtering (GDPR/CAN-SPAM)'
    ],
    howItWorks: [
      'Upload or sync your lead lists',
      'AI verifies each contact',
      'Removes invalid & risky emails',
      'Delivers clean, ready-to-send lists'
    ]
  },
  {
    id: 'handwritten-personalization',
    name: 'Handwritten AI',
    tagline: 'Human-Like Personalization',
    description: 'AI writes emails that feel personally crafted for each recipient.',
    detailedDescription: 'Our breakthrough AI technology creates emails so personalized, recipients think you spent 20 minutes researching and writing to them personally. No more generic templates.',
    icon: <PenTool className="w-6 h-6" />,
    replaces: 'Personal Assistant',
    replacesPrice: '$3K+/mo',
    stats: {
      label: 'Personal feel',
      value: '96%'
    },
    features: [
      'Deep prospect research integration',
      'Natural language variations',
      'Context-aware messaging',
      'Mimics your writing style'
    ],
    howItWorks: [
      'AI studies your best performing emails',
      'Researches each prospect individually',
      'Crafts unique messages for each person',
      'Maintains authentic, human tone'
    ]
  }
];

interface AllInOnePlatformProps {
  onStartClick?: () => void;
}

const AllInOnePlatform: React.FC<AllInOnePlatformProps> = ({ onStartClick }) => {
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  // Calculate total replacement cost
  const totalReplacementCost = products.reduce((total, product) => {
    const price = parseInt(product.replacesPrice.replace(/[^0-9]/g, ''));
    return total + price;
  }, 0);

  const toggleProduct = (productId: string) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">
            All-in-One Platform
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            12 Tools in 1 Simple Platform
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Replace your complex tech stack with one integrated solution that does it all.
          </p>
        </div>
        
        {/* Products */}
        <div className="space-y-4 mb-12">
          {products.map((product) => {
            const isExpanded = expandedProduct === product.id;
            
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
              >
                {/* Product Header */}
                <motion.div
                  className={`cursor-pointer ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors duration-200`}
                  onClick={() => toggleProduct(product.id)}
                >
                  <div className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 text-blue-600 p-3 rounded-lg">
                        {product.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>
                        <p className="text-gray-600">{product.tagline}</p>
                      </div>
                      <Badge variant="outline" className="ml-4 border-blue-200 text-blue-700">
                        Replaces: {product.replaces} (${product.replacesPrice})
                      </Badge>
                    </div>
                    <div className="flex items-center">
                      {product.stats && (
                        <div className="hidden md:block mr-4 text-right">
                          <div className="text-lg font-semibold text-gray-900">
                            {product.stats.value}
                          </div>
                          <div className="text-sm text-gray-500">
                            {product.stats.label}
                          </div>
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-4"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>

                {/* Expanded View */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-200"
                    >
                      <div className="p-8 space-y-8">
                        {/* Description */}
                        <div>
                          <p className="text-gray-700 text-lg leading-relaxed">
                            {product.detailedDescription}
                          </p>
                        </div>

                        {/* Video Demo */}
                        {product.videoUrl && (
                          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                            <iframe 
                              src={`${product.videoUrl}?autoplay=0`}
                              width="100%" 
                              height="100%" 
                              frameBorder="0" 
                              allow="fullscreen; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          </div>
                        )}

                        {/* Features and How it Works */}
                        <div className="grid md:grid-cols-2 gap-8">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-yellow-500" />
                              Key Features
                            </h4>
                            <ul className="space-y-2">
                              {product.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <Target className="w-5 h-5 text-blue-500" />
                              How It Works
                            </h4>
                            <ol className="space-y-2">
                              {product.howItWorks.map((step, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                  <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                    {idx + 1}
                                  </span>
                                  <span className="text-gray-700">{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="text-sm text-gray-600">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Setup time: ~2 minutes
                          </div>
                          <Button 
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={onStartClick}
                          >
                            Book a Call
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* ROI Calculator Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 mb-12 border border-green-200"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Your ROI with Munger.ai
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 text-center shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Cost per Meeting</div>
              <div className="text-3xl font-bold text-green-600 mb-1">$6.47 - $19.40</div>
              <div className="text-xs text-gray-500">Based on 5-15 meetings/week</div>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Typical Deal Size</div>
              <div className="text-3xl font-bold text-blue-600 mb-1">$5,000+</div>
              <div className="text-xs text-gray-500">Average B2B contract value</div>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-sm">
              <div className="text-sm text-gray-600 mb-2">Monthly ROI</div>
              <div className="text-3xl font-bold text-purple-600 mb-1">2,577%</div>
              <div className="text-xs text-gray-500">Based on typical deal sizes</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">ROI Breakdown:</h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Weekly meetings booked:</span>
                <span className="font-semibold">5-15 meetings</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Meeting → Deal conversion:</span>
                <span className="font-semibold">10-20% (industry avg)</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Expected monthly deals:</span>
                <span className="font-semibold">2-12 deals</span>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between items-center text-base">
                  <span className="font-semibold text-gray-900">Projected Monthly Revenue:</span>
                  <span className="font-bold text-green-600">$10,000 - $60,000</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Summary Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 text-center border border-blue-200"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Everything You Need to Scale Cold Email
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-8 mb-6">
            <div>
              <div className="text-3xl font-bold text-blue-600">${totalReplacementCost.toLocaleString()}+</div>
              <div className="text-sm text-gray-600">in tool costs replaced</div>
            </div>
            <div className="text-2xl font-bold text-gray-400">→</div>
            <div>
              <div className="text-3xl font-bold text-green-600">All-in-One</div>
              <div className="text-sm text-gray-600">everything included</div>
            </div>
          </div>
          <Button 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={onStartClick}
          >
            Book a Call
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default AllInOnePlatform; 